import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface EstablishmentSettings {
  id: string;
  name: string;
  slug: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  country: string | null;
  googleMapsUrl: string | null;
  googleBusinessUrl: string | null;
  websiteUrl: string | null;
  logoUrl: string | null;
  isActive: boolean;
  planType: string | null;
  trialEndsAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Review platform URLs
  tripadvisorReviewUrl: string | null;
  yelpReviewUrl: string | null;
  facebookReviewUrl: string | null;
  trustpilotReviewUrl: string | null;
  theforkReviewUrl: string | null;
  preferredReviewPlatform: string | null;
  // Booking notice
  bookingNotice: string | null;
  // Floor plan viewport
  floorPlanViewport: { zoom: number; panByZone: Record<string, { x: number; y: number }> } | null;
  // Floor plan v2 feature flag
  floorPlanV2Enabled: boolean;
}

export interface EstablishmentSettingsUpdate {
  name?: string;
  slug?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  country?: string | null;
  googleMapsUrl?: string | null;
  googleBusinessUrl?: string | null;
  websiteUrl?: string | null;
  logoUrl?: string | null;
  // Review platform URLs
  tripadvisorReviewUrl?: string | null;
  yelpReviewUrl?: string | null;
  facebookReviewUrl?: string | null;
  trustpilotReviewUrl?: string | null;
  theforkReviewUrl?: string | null;
  preferredReviewPlatform?: string | null;
  // Booking notice
  bookingNotice?: string | null;
  // Floor plan viewport
  floorPlanViewport?: { zoom: number; panByZone: Record<string, { x: number; y: number }> } | null;
}

export function useEstablishmentSettings(establishmentId?: string | null) {
  const queryClient = useQueryClient();

  const { data: settings, isLoading, error } = useQuery({
    queryKey: ['establishment-settings', establishmentId],
    queryFn: async (): Promise<EstablishmentSettings | null> => {
      if (!establishmentId) {
        console.warn('useEstablishmentSettings: No establishmentId provided');
        return null;
      }
      
      const { data, error } = await supabase
        .from('establishments' as any)
        .select('*')
        .eq('id', establishmentId)
        .single();

      if (error) {
        console.error('Error fetching establishment settings:', error);
        return null;
      }

      const estData = data as any;
      return {
        id: estData.id,
        name: estData.name,
        slug: estData.slug,
        email: estData.email,
        phone: estData.phone,
        address: estData.address,
        city: estData.city,
        postalCode: estData.postal_code,
        country: estData.country,
        googleMapsUrl: estData.google_maps_url,
        googleBusinessUrl: estData.google_business_url,
        websiteUrl: estData.website_url,
        logoUrl: estData.logo_url,
        isActive: estData.is_active,
        planType: estData.plan_type,
        trialEndsAt: estData.trial_ends_at,
        createdAt: estData.created_at,
        updatedAt: estData.updated_at,
        // Review platform URLs
        tripadvisorReviewUrl: estData.tripadvisor_review_url,
        yelpReviewUrl: estData.yelp_review_url,
        facebookReviewUrl: estData.facebook_review_url,
        trustpilotReviewUrl: estData.trustpilot_review_url,
        theforkReviewUrl: estData.thefork_review_url,
        preferredReviewPlatform: estData.preferred_review_platform,
        // Booking notice
        bookingNotice: estData.booking_notice,
        // Floor plan viewport
        floorPlanViewport: estData.floor_plan_viewport ?? null,
        floorPlanV2Enabled: !!estData.floor_plan_v2_enabled,
      };
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: EstablishmentSettingsUpdate) => {
      if (!settings?.id) {
        throw new Error('No establishment settings found');
      }

      // Reset lat/lng when address fields change to force re-geocoding
      const addressChanged = updates.address !== undefined || updates.city !== undefined
        || updates.postalCode !== undefined || updates.country !== undefined;

      const { error } = await supabase
        .from('establishments' as any)
        .update({
          name: updates.name,
          slug: updates.slug,
          email: updates.email,
          phone: updates.phone,
          address: updates.address,
          city: updates.city,
          postal_code: updates.postalCode,
          country: updates.country,
          google_maps_url: updates.googleMapsUrl,
          google_business_url: updates.googleBusinessUrl,
          website_url: updates.websiteUrl,
          logo_url: updates.logoUrl,
          // Review platform URLs
          tripadvisor_review_url: updates.tripadvisorReviewUrl,
          yelp_review_url: updates.yelpReviewUrl,
          facebook_review_url: updates.facebookReviewUrl,
          trustpilot_review_url: updates.trustpilotReviewUrl,
          thefork_review_url: updates.theforkReviewUrl,
          preferred_review_platform: updates.preferredReviewPlatform,
          // Booking notice
          booking_notice: updates.bookingNotice,
          // Floor plan viewport
          floor_plan_viewport: updates.floorPlanViewport,
          // Reset coordinates on address change
          ...(addressChanged ? { latitude: null, longitude: null } : {}),
        })
        .eq('id', settings.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishment-settings'] });
      toast({
        title: 'Paramètres sauvegardés',
        description: 'Les informations de l\'établissement ont été mises à jour.',
      });
    },
    onError: (error) => {
      console.error('Error updating establishment settings:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder les paramètres.',
        variant: 'destructive',
      });
    },
  });

  return {
    settings,
    isLoading,
    error,
    updateSettings: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    establishmentName: settings?.name || 'Mon établissement',
  };
}
